import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import Calendar from 'react-calendar'
import dayjs from 'dayjs'
import {
  selectAccountInfo,
  selectLocations,
  selectOrderMode,
  selectIsUpgrade,
  selectSelectedLocationId,
  selectOrderId,
  selectOnboardingContactForLocation,
  setOnboardingContactUseAccountOwner,
  setOnboardingContactField,
  updateLocation,
} from '../../../../app-store/PurchaseLinkSlice'
import {
  completeCurrentAndSetStep,
  STEPS,
} from '../../../../app-store/AccountSetupSlice'
import {getSchedulingLocations} from '../utils/getSchedulingLocations'
import {getRequiredCallTypes} from '../../../../utils/scheduling-call-rules'
import {selectEffectiveRole} from '../../../../app-store/selector'
import {
  REGION_OPTIONS,
  ONBOARDING_REGION_OPTIONS,
  isSpanishToggleEligible,
  isStandardRegionSelectEligible,
  isStarterRegionSelectEligible,
} from '../utils/schedulingLanguageRules'
import {
  useScheduledAppointments,
  useOnboardingScheduling,
} from '../hooks/useOnboardingScheduling'
import {useAuthClient} from '../../../../utils/use-auth-client'
import {OnboardingContact} from './OnboardingContact'
import {SchedulingBackButton} from './SchedulingBackButton'
import {validateOnboardingContact} from '../utils/onboardingContactValidation'
import {SkipCallButton, SkippedCallNotice} from './SkipCallButton'
import {hasPartnerOpsRole} from '../../../../utils/account-management-navigation'
import {isKeapPartnerSchedulingRole} from '../utils/keapPartnerSchedulingRules'
import {Button} from '../../../shared/Button'
import {Select} from '../../../shared/Select'
import Icon from '../../../../core/shared/Icon'
import {Tooltip} from '../../../../core/shared/Tooltip'
import {theme} from '../../../theme'
import {
  StepCardWrapper,
  StepCard,
  FooterRow,
  FooterCenter,
} from '../AccountSetup.styles'
import {
  Panel,
  PanelTitle,
  PageHeading,
  HeadingTitle,
  HeadingSubtitle,
  LocationSchedulePanel,
  SchedulingLayout,
  LocationDetailPanel,
  LocationDetailStack,
  CallSectionTitle,
  LocationTabList,
  LocationTabCard,
  LocationTabRow,
  LocationTabMainIcon,
  LocationTabDetailsCol,
  LocationTabTopRow,
  LocationTabIndexLabel,
  LocationTabName,
  LocationTabMeta,
  LocationTabTimezone,
  LocationTabBadge,
  ScheduleColumns,
  ScheduleColumn,
  ColumnLabel,
  CalendarWrap,
  SlotGrid,
  SlotBtn,
  SlotsEmpty,
  SlotsLoading,
  SlotsSpinner,
  ScheduledNotice,
  ScheduledNoticeText,
  ResetButton,
  ScheduleCallButton,
  ScheduleActionsRow,
  ErrorText,
  LanguageRegionSection,
  LanguageRegionRow,
  LanguageRegionLabel,
  LanguageToggle,
  LanguageOption,
  RegionSelectWrap,
} from './SchedulingStep.styles'

const calendarMaxDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
const SEO_PRODUCT_CODE = 'seobase-5-thryv-m2m'
const SCC_PRODUCT_CODE = 'addon-5-stratcontent-m2m'

function hasNewSeoProduct(items = []) {
  const flat = items.flatMap(item => [item, ...(item?.addons ?? [])])
  return flat.some(item => {
    const code = item?.productCode || item?.code
    const isNew = !(
      item?.alreadyIncluded === true || item?.isAlreadyAdded === true
    )
    return code === SEO_PRODUCT_CODE && isNew
  })
}

function hasNewSccProduct(items = []) {
  const flat = items.flatMap(item => [item, ...(item?.addons ?? [])])
  return flat.some(item => {
    const code = item?.productCode || item?.code
    const isNew = !(
      item?.alreadyIncluded === true || item?.isAlreadyAdded === true
    )
    return code === SCC_PRODUCT_CODE && isNew
  })
}

// `existingAppointment.date` is a plain calendar date with no time-of-day
// meaning, but knex/mysql2 serializes DATE columns as an ISO string at UTC
// midnight (e.g. "2026-09-17T00:00:00.000Z") — passing that straight to
// dayjs().format() converts to the browser's local zone first, rolling the
// date back a day for anyone west of UTC. Read the YYYY-MM-DD substring as a
// bare date literal instead so no zone conversion ever happens.
function formatApptDate(date) {
  if (!date) return ''
  return dayjs(String(date).slice(0, 10)).format('MMM D, YYYY')
}

// Short zone label for the booked call, e.g. "America/Chicago" → "CST"/"CDT"
// (the abbreviation flips with daylight saving, so resolve it against the
// booked date, not today). Same approach as ProposalTool's
// ClientProposalSchedule formatTimeZoneLabel.
function formatTimeZoneLabel(tzId, refDate) {
  if (!tzId) return ''
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tzId,
      timeZoneName: 'short',
    }).formatToParts(refDate ? new Date(refDate) : new Date())
    return parts.find(p => p.type === 'timeZoneName')?.value || tzId
  } catch {
    return tzId
  }
}

// "Friday, June 10, 2026 at 09:15 AM CST" — the legacy onboarding-program
// confirmation wording. `date` (DATE column, see formatApptDate) and `time`
// (HH:mm:ss, stored in the call's own zone — see useOnboardingScheduling's
// handleScheduleCall) are combined as a bare literal so no browser-zone
// conversion happens. A row with no usable time still shows its day.
function formatScheduledDateTime(row) {
  if (!row?.date) return ''
  const datePart = String(row.date).slice(0, 10)
  const timeMatch = String(row.time ?? '').match(/^(\d{1,2}):(\d{2})/)
  const timePart = timeMatch
    ? `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}:00`
    : null

  const day = dayjs(datePart)
  if (!day.isValid()) return formatApptDate(row.date)
  const dayLabel = day.format('dddd, MMMM D, YYYY')

  if (!timePart) return dayLabel
  const local = dayjs(`${datePart}T${timePart}`)
  const timeLabel = local.isValid() ? local.format('hh:mm A') : null
  if (!timeLabel) return dayLabel

  const tz = formatTimeZoneLabel(row.timezone, `${datePart}T${timePart}`)
  return `${dayLabel} at ${timeLabel}${tz ? ` ${tz}` : ''}`
}

// One scheduler panel in the right-hand column: the calendar/slot picker (or
// the already-scheduled summary) for ONE named call (Thryv Kickoff Call /
// Thryv Success Services Call — see CALL_TYPES) of whichever location is
// currently selected in the left-hand LocationTabList. The tab already shows
// the location's name/timezone/status; the panel is headed by the call's
// name, matching the legacy onboarding-program screen's per-call sections.
// Mounted fresh (keyed by locationId + call key in SchedulingStep) each time
// the active location changes, so in-progress date/slot selection never
// leaks between locations or calls. onTimezoneResolved reports the timezone
// up to SchedulingStep once a real (user-triggered) fetch resolves one, so
// the corresponding LocationTab can show it too — see
// useOnboardingScheduling: there's no silent background fetch on mount, so
// a location's timezone is only ever known once a date picker has been used.
function LocationScheduleDetail({
  location,
  accountInfo,
  orderId,
  callType,
  callTypesForLocation,
  existingAppointment,
  currentAppointments,
  onboardingContact,
  onboardingContactValid,
  isSkipped,
  onSkip,
  onUndoSkip,
  onChanged,
  onTimezoneResolved,
  calendarMinDate,
}) {
  const isUpgrade = useSelector(selectIsUpgrade)
  const isTss = callType.key === 'thryv-success-services'
  const effectiveMaxDate =
    isTss && hasNewSccProduct(location?.items ?? []) && !isUpgrade
      ? dayjs().add(60, 'day').toDate()
      : calendarMaxDate

  const isPartnerOps = hasPartnerOpsRole(useSelector(selectEffectiveRole))
  const _roles = useSelector(selectEffectiveRole)
  const isPartnerChannel = isKeapPartnerSchedulingRole(
    Array.isArray(_roles) ? _roles : _roles ? [_roles] : [],
  )
  // Spanish toggle + Region select — Kickoff call only, per legacy
  // date-time-modal.js (see schedulingLanguageRules.js). Local to this one
  // location+callType instance, so each location's own choice is
  // independent and resets when the rep switches to another location.
  const isKickoff = callType.key === 'kickoff'
  const [spanish, setSpanish] = useState(false)
  const [region, setRegion] = useState('')

  const showSpanishToggle =
    isKickoff && !isPartnerChannel && isSpanishToggleEligible(location)
  const showStarterRegionSelect =
    isKickoff && !isPartnerChannel && isStarterRegionSelectEligible(location)
  const showStandardRegionSelect =
    isKickoff &&
    !isPartnerChannel &&
    isStandardRegionSelectEligible(location, spanish)

  // legacy date-time-modal.js's openCalendar gate: once either Region select
  // is on-screen, the calendar stays hidden until "Yes" is picked for
  // Spanish or a region is chosen — a location with neither control showing
  // never had a calendar to gate in the first place.
  // Partner channel: region is never required, so bypass the gate even when
  // the Spanish toggle is visible.
  const languageRegionControlsShown =
    showSpanishToggle || showStandardRegionSelect || showStarterRegionSelect
  const showCalendar =
    !languageRegionControlsShown ||
    isPartnerChannel ||
    spanish === true ||
    region !== ''

  const {
    selectedDate,
    slots,
    selectedSlot,
    timeZoneId,
    loadingSlots,
    scheduling,
    cancelling,
    scheduleError,
    setSelectedSlot,
    handleDateChange,
    handleScheduleCall,
    handleCancelSchedule,
  } = useOnboardingScheduling({
    location,
    accountInfo,
    orderId,
    callType,
    existingAppointment,
    currentAppointments,
    onboardingContact,
    languagePreference: spanish ? 'es' : 'en',
    region,
    onScheduled: onChanged,
    onCancelled: onChanged,
  })

  // onTimezoneResolved deliberately left out of deps — it's a new inline
  // closure every SchedulingStep render, and including it would re-fire
  // this (and the setState it causes) on every render instead of only when
  // timeZoneId itself actually changes.
  React.useEffect(() => {
    if (timeZoneId) onTimezoneResolved?.(timeZoneId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeZoneId])

  // Caps the slot list to the calendar's actual rendered height so it
  // scrolls instead of growing the ScheduleColumns row taller than the
  // calendar — a CSS grid row's "auto" height is sized to fit its tallest
  // item's full content, so SlotGrid's own overflow-y: auto never kicks in
  // on its own once there are more slots than fit beside the calendar.
  const calendarRef = React.useRef(null)
  const [calendarHeight, setCalendarHeight] = useState(null)

  React.useEffect(() => {
    const node = calendarRef.current
    if (!node || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      setCalendarHeight(entry.contentRect.height)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [showCalendar])

  return (
    <LocationDetailPanel>
      <CallSectionTitle>
        <span>{callType.label}</span>
      </CallSectionTitle>
      {existingAppointment ? (
        <ScheduledNotice>
          <Icon name="calendar-day" size="md" />
          <ScheduledNoticeText>
            Thank you, your <u>{callType.label}</u> is scheduled for{' '}
            <b>{formatScheduledDateTime(existingAppointment)}</b>.
          </ScheduledNoticeText>
          <ResetButton
            type="button"
            disabled={cancelling}
            onClick={handleCancelSchedule}
            aria-label={`Reset ${callType.label}`}
          >
            <Icon name="reset" size="sm" />
            {cancelling ? 'Resetting…' : 'Reset'}
          </ResetButton>
        </ScheduledNotice>
      ) : isSkipped ? (
        <SkippedCallNotice callType={callType} onUndoSkip={onUndoSkip} />
      ) : (
        <>
          {(showSpanishToggle ||
            showStandardRegionSelect ||
            showStarterRegionSelect) && (
            <LanguageRegionSection>
              {showSpanishToggle && (
                <LanguageRegionRow>
                  <LanguageRegionLabel>
                    Would you like to schedule this call in Spanish?
                  </LanguageRegionLabel>
                  <LanguageToggle>
                    <LanguageOption
                      type="button"
                      active={spanish}
                      onClick={() => setSpanish(true)}
                    >
                      Yes
                    </LanguageOption>
                    <LanguageOption
                      type="button"
                      active={!spanish}
                      onClick={() => setSpanish(false)}
                    >
                      No
                    </LanguageOption>
                  </LanguageToggle>
                </LanguageRegionRow>
              )}
              {showStandardRegionSelect && (
                <RegionSelectWrap>
                  <LanguageRegionLabel>Select Region</LanguageRegionLabel>
                  <Select
                    variant="form"
                    size="sm"
                    options={REGION_OPTIONS}
                    value={region}
                    onChange={e => setRegion(e.target.value)}
                  />
                </RegionSelectWrap>
              )}
              {showStarterRegionSelect && (
                <RegionSelectWrap>
                  <LanguageRegionLabel>Select Region</LanguageRegionLabel>
                  <Select
                    variant="form"
                    size="sm"
                    options={ONBOARDING_REGION_OPTIONS}
                    value={region}
                    onChange={e => setRegion(e.target.value)}
                  />
                </RegionSelectWrap>
              )}
            </LanguageRegionSection>
          )}
          {showCalendar ? (
            <>
              <ScheduleColumns>
                <ScheduleColumn>
                  <ColumnLabel>
                    <Icon
                      name="calendar"
                      size="xs"
                      color={theme.colors.text.secondary}
                    />
                    Select a Date
                  </ColumnLabel>
                  <CalendarWrap ref={calendarRef}>
                    <Calendar
                      value={selectedDate}
                      onChange={handleDateChange}
                      minDate={calendarMinDate}
                      maxDate={effectiveMaxDate}
                      locale="en-US"
                    />
                  </CalendarWrap>
                </ScheduleColumn>

                <ScheduleColumn>
                  <ColumnLabel>
                    <Icon
                      name="clock"
                      size="xs"
                      color={theme.colors.text.secondary}
                    />
                    Available Time Slot
                  </ColumnLabel>
                  <SlotGrid
                    style={
                      calendarHeight ? {maxHeight: calendarHeight} : undefined
                    }
                  >
                    {!selectedDate ? (
                      <SlotsEmpty>
                        Select a date to see available time slots.
                      </SlotsEmpty>
                    ) : loadingSlots ? (
                      <SlotsLoading>
                        <SlotsSpinner />
                        <span>Loading available times…</span>
                      </SlotsLoading>
                    ) : slots.length === 0 ? (
                      <SlotsEmpty>No times available for this date.</SlotsEmpty>
                    ) : (
                      slots.map((slot, i) => (
                        <SlotBtn
                          key={slot.startTime || i}
                          type="button"
                          selected={selectedSlot?.startTime === slot.startTime}
                          onClick={() => setSelectedSlot(slot)}
                        >
                          {slot.displayTime}
                        </SlotBtn>
                      ))
                    )}
                  </SlotGrid>
                  {scheduleError && <ErrorText>{scheduleError}</ErrorText>}
                </ScheduleColumn>
              </ScheduleColumns>

              <ScheduleActionsRow>
                {/* Per-call skip is off for every role (the admin Skip in the
                footer replaces it) except PartnerOps, whose legacy scheduler
                let the rep skip any call isCallSkippable allows. This branch
                already excludes booked / skipped calls. */}
                {isPartnerOps && (
                  <SkipCallButton
                    location={location}
                    callType={callType}
                    callTypesForLocation={callTypesForLocation}
                    onSkip={onSkip}
                  />
                )}
                <ScheduleCallButton
                  variant="primary"
                  size="sm"
                  isLoading={scheduling}
                  disabled={
                    !selectedSlot || scheduling || !onboardingContactValid
                  }
                  onClick={handleScheduleCall}
                  leftIcon={<Icon name="calendar" size="sm" color="#fff" />}
                >
                  Schedule Call
                </ScheduleCallButton>
              </ScheduleActionsRow>
              {!onboardingContactValid && (
                <ErrorText>
                  Fill in the Onboarding Contact&apos;s name, email and phone
                  (or check &quot;Same as account owner&quot;) before
                  scheduling.
                </ErrorText>
              )}
            </>
          ) : (
            <SlotsEmpty>
              Select a region above to see available dates and times.
            </SlotsEmpty>
          )}
        </>
      )}
    </LocationDetailPanel>
  )
}

// One entry in the left-hand LocationTabList. callTypes are the named calls
// this location's own cart requires (getRequiredCallTypes) — the card does
// NOT list them (the call names live on the right-hand scheduler panels
// only); they're used here so the badge reads done once every one of them
// is resolved, whether booked OR skipped (see SkipCallButton.js). A skipped
// call unblocks Continue the same as a booking, but it isn't one, so the
// badge tracks bookedCount/skippedCount separately and only calls it
// "Scheduled" when every resolved call was an actual booking — a location
// resolved entirely (or only partly) via Skip reads as "Skipped" instead, so
// a rep never mistakes a skip for a real appointment at a glance.
// resolvedTimezone comes from SchedulingStep's per-location map, populated
// only once one of that location's LocationScheduleDetails has actually
// resolved one (i.e. a date picker has been used) — no background fetch
// happens here, so a location shows no timezone at all until then.
function LocationTab({
  location,
  index,
  active,
  callTypes,
  appointments,
  skippedCalls,
  resolvedTimezone,
  onSelect,
}) {
  const bookedCount = callTypes.filter(ct => appointments?.[ct.key]).length
  const skippedCount = callTypes.filter(
    ct => !appointments?.[ct.key] && skippedCalls?.[ct.key],
  ).length
  const resolvedCount = bookedCount + skippedCount
  const done = callTypes.length > 0 && resolvedCount === callTypes.length
  const allSkipped = done && skippedCount === callTypes.length
  const timezone =
    callTypes.map(ct => appointments?.[ct.key]?.timezone).find(Boolean) ||
    resolvedTimezone

  return (
    <LocationTabCard
      as="button"
      type="button"
      active={active}
      onClick={onSelect}
    >
      <LocationTabRow>
        <LocationTabMainIcon>
          <Icon name="calendar-day" size="sm" style={{width: 12, height: 12}} />
        </LocationTabMainIcon>
        <LocationTabDetailsCol>
          <LocationTabTopRow>
            <LocationTabIndexLabel>
              <Icon name="loc-ind" size="xxs" />
              Location {index + 1}
            </LocationTabIndexLabel>
            <LocationTabBadge done={done} skipped={allSkipped}>
              {done
                ? allSkipped
                  ? 'Skipped'
                  : 'Scheduled'
                : callTypes.length > 1 && resolvedCount > 0
                ? `${resolvedCount}/${callTypes.length} Scheduled`
                : 'Pending'}
            </LocationTabBadge>
          </LocationTabTopRow>
          <Tooltip
            block
            size="lg"
            variant="top"
            description={location.locationName || location.thryvId}
          >
            <LocationTabName>
              {location.locationName || location.thryvId}
            </LocationTabName>
          </Tooltip>
          {timezone && (
            <LocationTabMeta>
              <Icon name="clock" size="xxs" color={theme.colors.text.primary} />
              <LocationTabTimezone>{timezone}</LocationTabTimezone>
            </LocationTabMeta>
          )}
        </LocationTabDetailsCol>
      </LocationTabRow>
    </LocationTabCard>
  )
}

// Reached from Catalog Selection's Continue button when at least one
// relevant location's cart needs an onboarding call (see
// getSchedulingLocations). Shows one card per QUALIFYING location — always,
// even when only one location qualifies (the user's explicit call, so the
// rep always sees which location and which call(s) they're booking) —
// scheduling is decided and booked per location AND per named call, not
// once for the combined cart, even though every location here shares one
// order_id (see useOnboardingScheduling's locationInteractionTag +
// callTypeForAppointmentRow for how appointments are told apart under it).
export function SchedulingStep({onCancel, isAdmin}) {
  const dispatch = useDispatch()
  const accountInfo = useSelector(selectAccountInfo)
  const locations = useSelector(selectLocations)
  const orderMode = useSelector(selectOrderMode)
  const isUpgrade = useSelector(selectIsUpgrade)
  const selectedLocationId = useSelector(selectSelectedLocationId)
  const orderId = useSelector(selectOrderId)
  const roles = useSelector(selectEffectiveRole)
  const client = useAuthClient()

  const [scheduleOffsetDetails, setScheduleOffsetDetails] = useState([])
  useEffect(() => {
    async function fetchScheduleOffsetDays() {
      try {
        const response = await client('cc/order/schedule-offset-days')
        if (response) setScheduleOffsetDetails(response)
      } catch {
        // non-fatal; calendarMinDate falls back to today
      }
    }
    fetchScheduleOffsetDays()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function getCalendarMinDate(items, callType) {
    if (callType?.key === 'thryv-success-services' && hasNewSccProduct(items)) {
      return isUpgrade
        ? dayjs().add(2, 'day').toDate() // upgrade SCC: 2–30 days
        : dayjs().add(30, 'day').toDate() // new SCC: 30–60 days
    }
    if (!hasNewSeoProduct(items)) return new Date()
    const offsetDetails = scheduleOffsetDetails.find(
      d => d.product_code === SEO_PRODUCT_CODE,
    )
    if (!offsetDetails?.offset_start_day) return new Date()
    return dayjs().add(offsetDetails.offset_start_day, 'day').toDate()
  }

  const schedulingLocations = getSchedulingLocations({
    locations,
    orderMode,
    selectedLocationId,
    roles,
  })

  const {appointmentsByLocationId, loading, refetch} =
    useScheduledAppointments(orderId)

  // Mirror each location's own booked appointment ids onto PurchaseLinkSlice.locations
  // so buildSendLinkPayload.js's locationDetails (built later, from `locations`, with no
  // access to this hook) can send them per-location — order_header only has one flat
  // onboarding_appt_id/thryv_success_services_appt_id for the whole combined order, so
  // this is the only place a per-location value is available before send-link.
  useEffect(() => {
    for (const loc of schedulingLocations) {
      const appts = appointmentsByLocationId[loc.locationId]
      if (!appts) continue
      dispatch(
        updateLocation({
          locationId: loc.locationId,
          changes: {
            onboardingApptId: appts.kickoff?.appt_id ?? loc.onboardingApptId,
            thryvSuccessServicesApptId:
              appts['thryv-success-services']?.appt_id ??
              loc.thryvSuccessServicesApptId,
          },
          skipDirty: true,
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentsByLocationId])

  // Which named call(s) each location's own cart requires — Thryv Kickoff
  // Call and/or Thryv Success Services Call (see getRequiredCallTypes for
  // the legacy SCC-only vs both rule). Drives the per-call scheduler panels
  // on the right, the call list on each tab, and the Continue gate.
  const callTypesByLocationId = Object.fromEntries(
    schedulingLocations.map(loc => [
      loc.locationId,
      getRequiredCallTypes(loc.items ?? []),
    ]),
  )

  // Per-location, per-call-type skip state — local to this step (nothing is
  // sent to the backend for a skipped call, unlike a real booking), mirrors
  // legacy onboarding-program.js's own local isApptSkipped state. Keyed by
  // locationId then callKey so Multi-Location Parent Pay's separate
  // locations/calls never leak into each other. See SkipCallButton.js for
  // the eligibility rule and SkippedCallNotice for the "undo" affordance.
  const [skippedCallsByLocationId, setSkippedCallsByLocationId] = useState({})

  const handleSkipCall = (locationId, callKey) => {
    setSkippedCallsByLocationId(prev => ({
      ...prev,
      [locationId]: {...(prev[locationId] ?? {}), [callKey]: true},
    }))
  }

  const handleUndoSkipCall = (locationId, callKey) => {
    setSkippedCallsByLocationId(prev => {
      const locationSkips = {...(prev[locationId] ?? {})}
      delete locationSkips[callKey]
      return {...prev, [locationId]: locationSkips}
    })
  }

  const allScheduled =
    schedulingLocations.length > 0 &&
    schedulingLocations.every(loc =>
      callTypesByLocationId[loc.locationId].every(
        ct =>
          appointmentsByLocationId[loc.locationId]?.[ct.key] ||
          skippedCallsByLocationId[loc.locationId]?.[ct.key],
      ),
    )

  const [activeLocationId, setActiveLocationId] = useState(
    () => schedulingLocations[0]?.locationId ?? null,
  )
  const activeLocation =
    schedulingLocations.find(loc => loc.locationId === activeLocationId) ??
    schedulingLocations[0] ??
    null

  // Populated per-location only once that location's own
  // LocationScheduleDetail resolves a real timezone (i.e. its date picker
  // has been used) — see LocationScheduleDetail's onTimezoneResolved.
  const [locationTimezones, setLocationTimezones] = useState({})

  // Onboarding Contact is per-location — Multi-Location Parent Pay books a
  // separate kickoff/TSS call per location, each of which may have its own
  // contact, so the active tab's location owns this render's contact value
  // (see PurchaseLinkSlice's onboardingContactByLocationId).
  const onboardingContact = useSelector(state =>
    selectOnboardingContactForLocation(state, activeLocation?.locationId),
  )

  // The onboarding contact's "Same as account owner" default and phone
  // validation both key off THIS location's own identity, not the parent's
  // — Multi-Location Parent Pay books a separate call per location, each of
  // which may have its own account owner, so `activeLocation.accountInfo`
  // (see PurchaseLinkSlice's locations[].accountInfo) is preferred even
  // under 'parentPay' orderMode.
  const identity = activeLocation?.accountInfo ?? accountInfo

  // The actual name/email/phone sent to the call-booking API (plans/onboarding)
  // — the account owner's info unless the rep unchecked "Same as account
  // owner" and typed a different onboarding contact for this location. See
  // useOnboardingScheduling's handleScheduleCall.
  const resolvedOnboardingContact = onboardingContact.useAccountOwner
    ? {
        firstName: identity?.firstName || '',
        lastName: identity?.lastName || '',
        email: identity?.email || '',
        mobile: identity?.mobile || identity?.phone || '',
      }
    : {
        firstName: onboardingContact.firstName,
        lastName: onboardingContact.lastName,
        email: onboardingContact.email,
        mobile: onboardingContact.mobile,
      }
  const isOnboardingContactValid =
    Object.keys(
      validateOnboardingContact(resolvedOnboardingContact, identity?.country),
    ).length === 0

  // Once any call is scheduled for THIS location, its Onboarding Contact has
  // already gone out to the call-booking API (and mirrored onto
  // order_header — see useOnboardingScheduling's syncOrderHeaderAppointment)
  // — lock it from further edits so what's on screen can't drift from what
  // was actually sent. Other locations' contacts stay editable.
  const isActiveLocationCallScheduled =
    Object.keys(appointmentsByLocationId[activeLocation?.locationId] ?? {})
      .length > 0

  const handleContinue = () => {
    if (!allScheduled) return
    const nextStepIndex = STEPS.findIndex(s => s.id === 'payment-details')
    dispatch(completeCurrentAndSetStep(nextStepIndex))
  }

  // Admin-only escape hatch — bypasses the allScheduled gate so an order
  // can proceed to payment without an onboarding call booked.
  const handleSkip = () => {
    const nextStepIndex = STEPS.findIndex(s => s.id === 'payment-details')
    dispatch(completeCurrentAndSetStep(nextStepIndex))
  }

  return (
    <>
      <PageHeading>
        <HeadingTitle>Schedule Onboarding Call</HeadingTitle>
        <HeadingSubtitle>
          {schedulingLocations.length > 1
            ? `${schedulingLocations.length} locations in this order need an onboarding call.`
            : 'This order needs an onboarding call before payment.'}
        </HeadingSubtitle>
      </PageHeading>

      <StepCardWrapper>
        <StepCard isSchedulingStep>
          <LocationSchedulePanel>
            {loading ? (
              <Panel>
                <PanelTitle>Loading scheduling status…</PanelTitle>
              </Panel>
            ) : (
              <SchedulingLayout>
                <LocationTabList>
                  {schedulingLocations.map((loc, index) => (
                    <LocationTab
                      key={loc.locationId}
                      location={loc}
                      index={index}
                      active={loc.locationId === activeLocation?.locationId}
                      callTypes={callTypesByLocationId[loc.locationId]}
                      appointments={appointmentsByLocationId[loc.locationId]}
                      skippedCalls={skippedCallsByLocationId[loc.locationId]}
                      resolvedTimezone={locationTimezones[loc.locationId]}
                      onSelect={() => setActiveLocationId(loc.locationId)}
                    />
                  ))}
                </LocationTabList>

                {activeLocation && (
                  <LocationDetailStack>
                    <OnboardingContact
                      contact={onboardingContact}
                      resolvedContact={resolvedOnboardingContact}
                      country={identity?.country}
                      locked={isActiveLocationCallScheduled}
                      onFieldChange={(field, value) =>
                        dispatch(
                          setOnboardingContactField({
                            locationId: activeLocation.locationId,
                            field,
                            value,
                          }),
                        )
                      }
                      onToggleUseAccountOwner={checked =>
                        dispatch(
                          setOnboardingContactUseAccountOwner({
                            locationId: activeLocation.locationId,
                            useAccountOwner: checked,
                          }),
                        )
                      }
                    />
                    {callTypesByLocationId[activeLocation.locationId].map(
                      callType => (
                        <LocationScheduleDetail
                          key={`${activeLocation.locationId}:${callType.key}`}
                          location={activeLocation}
                          accountInfo={accountInfo}
                          orderId={orderId}
                          callType={callType}
                          callTypesForLocation={
                            callTypesByLocationId[activeLocation.locationId]
                          }
                          existingAppointment={
                            appointmentsByLocationId[
                              activeLocation.locationId
                            ]?.[callType.key]
                          }
                          currentAppointments={
                            appointmentsByLocationId[activeLocation.locationId]
                          }
                          onboardingContact={resolvedOnboardingContact}
                          onboardingContactValid={isOnboardingContactValid}
                          isSkipped={Boolean(
                            skippedCallsByLocationId[
                              activeLocation.locationId
                            ]?.[callType.key],
                          )}
                          onSkip={() =>
                            handleSkipCall(
                              activeLocation.locationId,
                              callType.key,
                            )
                          }
                          onUndoSkip={() =>
                            handleUndoSkipCall(
                              activeLocation.locationId,
                              callType.key,
                            )
                          }
                          onChanged={refetch}
                          onTimezoneResolved={tz =>
                            setLocationTimezones(prev => ({
                              ...prev,
                              [activeLocation.locationId]: tz,
                            }))
                          }
                          calendarMinDate={getCalendarMinDate(
                            activeLocation.items ?? [],
                            callType,
                          )}
                        />
                      ),
                    )}
                  </LocationDetailStack>
                )}
              </SchedulingLayout>
            )}
          </LocationSchedulePanel>
        </StepCard>

        <SchedulingStepFooter
          onCancel={onCancel}
          backButton={
            <SchedulingBackButton
              appointmentsByLocationId={appointmentsByLocationId}
              locations={locations}
              accountInfo={accountInfo}
              onCancelFailed={refetch}
            />
          }
          onContinue={handleContinue}
          canContinue={allScheduled}
          isAdmin={isAdmin}
          onSkip={handleSkip}
        />
      </StepCardWrapper>
    </>
  )
}

// This step's own footer card — a sibling below StepCard (see
// AccountSetup/index.js), not inside it. "Continue to Payment" lives here
// (right side) rather than in the body, matching every other step's footer
// layout — Scheduling still decides per-location whether it's allowed via
// canContinue (allScheduled).
export function SchedulingStepFooter({
  onCancel,
  backButton,
  onContinue,
  canContinue,
  isAdmin,
  onSkip,
}) {
  return (
    <FooterRow>
      {/* Back | Cancel as one joined pill — same look as the Proposal Tool's
          schedule-appointment page (ClientProposalSchedule's ActionLeft).
          Back's behavior lives in SchedulingBackButton, passed in as a slot. */}
      <FooterCenter style={{gap: 0}}>
        {backButton}
        <Button
          variant="secondary-gray"
          size="md"
          type="button"
          onClick={onCancel}
          style={{borderRadius: backButton ? '0 80px 80px 0' : '80px'}}
        >
          Cancel
        </Button>
      </FooterCenter>

      <FooterCenter>
        {isAdmin && !canContinue && (
          <Button variant="secondary" size="sm" onClick={onSkip}>
            Skip
          </Button>
        )}
        <Button
          variant="solid-gradiant"
          size="sm"
          disabled={!canContinue}
          onClick={onContinue}
        >
          Continue to Payment &gt;
        </Button>
      </FooterCenter>
    </FooterRow>
  )
}
